import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { nativeSetItem, nativeGetItem, nativeRemoveItem, isNative } from '../lib/nativeBridge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('iems_token'));
  const [loading, setLoading] = useState(true);

  // Hydrate token from native storage on mobile.
  useEffect(() => {
    if (!isNative()) return;
    (async () => {
      const stored = await nativeGetItem('iems_token');
      if (stored && stored !== token) {
        localStorage.setItem('iems_token', stored);
        setToken(stored);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('iems_token', newToken);
    nativeSetItem('iems_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('iems_token');
    nativeRemoveItem('iems_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // Permission helper functions
  const hasPermission = (permission) => {
    return user?.permissions?.[permission] === true;
  };

  const canAccessFinance = () => {
    return hasPermission('can_access_finance_workspace') || 
           user?.access_level === 'super_admin' || 
           user?.access_level === 'admin';
  };

  const canViewAllRecords = () => {
    return hasPermission('can_view_all_records');
  };

  const canViewTeamRecords = () => {
    return hasPermission('can_view_team_records');
  };

  const isAdmin = () => {
    return user?.access_level === 'super_admin' || user?.access_level === 'admin';
  };

  const isManager = () => {
    return user?.access_level === 'manager' || isAdmin();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      isAuthenticated: !!user,
      hasPermission,
      canAccessFinance,
      canViewAllRecords,
      canViewTeamRecords,
      isAdmin,
      isManager
    }}>
      {children}
    </AuthContext.Provider>
  );
};
